import { Dayts, createNotification, getClasses, saveFile } from '@/utils';
import { MouseEvent, ReactElement } from 'react';

import Button from '@/common/Button';
import { DATETIME_FE_FORMAT_SHORT } from '@/constants';
import { Import } from '@/models/gen/graphql';
import Input from '@/common/Input';
import { ManifestsTypes } from '@/features/Manifests/types';
import Table from '@/common/Table';
import Tippy from '@tippyjs/react';
import { Toast } from '@/models';
import { getManifestTableGridTemplateColumns } from './utils';
import runDownloadManifestImports from '@/features/Manifests/api/services/runDownloadManifestImports';
import { useManifestsState } from '@/features/Manifests';
import { useNavigate } from 'react-router-dom';

const ManifestTableRow = ({ id, createdAt, status, statusDisplayName, provider, importedBy, type, email }: Import): ReactElement => {
  const navigate = useNavigate();
  const [{ isSelected, onSelect, handleDoubleClick, settings }] = useManifestsState(({ state, setState }) => [
    {
      isSelected: state.selected.includes(id),
      onSelect: (): void => state.onSelect(id),
      handleDoubleClick: (event: MouseEvent<HTMLTableRowElement>): void => {
        event.preventDefault();
        event.stopPropagation();
        if (ManifestsTypes.ReadyToImportStatuses.includes(status)) {
          // Navaigate to details page if manifest is ready to be imported
          navigate(`/manifests/${id}`);
        } else {
          // Otherwise, open override modal to allow user to override status to ready to import
          setState((current) => ({
            ...current,
            modals: {
              ...current.modals,
              manifestOverrides: { show: true, manifestId: id, name: current.imports.find((imp) => imp.id === id)?.name },
            },
          }));
        }
      },
      settings: state.settings,
    },
  ]);

  const onDownload = async (): Promise<void> => {
    try {
      const [res] = await runDownloadManifestImports(id);
      if (res?.error?.code || !res?.url) throw new Error(res.error?.message || 'Error downloading manifest');
      saveFile(res?.url);
    } catch (err) {
      console.error('Error downloading manifest:', err);
      createNotification(err.message || 'Error downloading manifest', Toast.Type.DANGER, 'Download Failed');
    }
  };

  const gridTemplateColumns = getManifestTableGridTemplateColumns(settings);

  return (
    <Table.Row id={id} className={getClasses(isSelected && 'selected')} onDoubleClick={handleDoubleClick} style={{ gridTemplateColumns }}>
      {settings.columns.SELECT && (
        <Table.Cell className="SELECT">
          <Input type="checkbox" onClick={onSelect} checked={isSelected} />
        </Table.Cell>
      )}
      {settings.columns.DATE && (
        <Table.Cell className="DATE" data-placeholder="--">
          {createdAt ? new Dayts(createdAt).toLocal().format(DATETIME_FE_FORMAT_SHORT) : ''}
        </Table.Cell>
      )}
      {settings.columns.STATUS && (
        <Table.Cell className={getClasses('STATUS', status)} data-placeholder="--">
          {statusDisplayName || ''}
        </Table.Cell>
      )}
      {settings.columns.PROVIDER && (
        <Table.Cell className="PROVIDER" data-placeholder="--">
          {provider?.displayName || ''}
        </Table.Cell>
      )}
      {settings.columns.IMPORTED_BY && (
        <Table.Cell className="IMPORTED-BY" data-placeholder="--">
          {importedBy || ''}
        </Table.Cell>
      )}
      {settings.columns.FROM && (
        <Table.Cell className="FROM" data-placeholder="--">
          {email?.from || ''}
        </Table.Cell>
      )}
      {settings.columns.SUBJECT && (
        <Table.Cell className="SUBJECT" data-placeholder="--">
          {email?.subject || ''}
        </Table.Cell>
      )}
      {settings.columns.TYPE && (
        <Table.Cell className="TYPE" data-placeholder="--">
          {type || ''}
        </Table.Cell>
      )}
      {settings.columns.DOWNLOAD && (
        <Table.Cell className="DOWNLOAD" data-placeholder="--">
          <Tippy content="Download" placement="top">
            <Button variant="icon" onClick={onDownload}>
              <i className="sv sv-download2 fs-4" />
            </Button>
          </Tippy>
        </Table.Cell>
      )}
    </Table.Row>
  );
};

export default ManifestTableRow;
